import "./styles.scss";

import { Breadcrumbs, CrumbProps } from "@amzn/stencil-react-components/breadcrumbs";
import { Expander } from "@amzn/stencil-react-components/expander";
import { Col, Container, Hr, Row, View } from "@amzn/stencil-react-components/layout";
import { PageContainer } from "@amzn/stencil-react-components/page";
import { SideNavItem, SideNavSection } from "@amzn/stencil-react-components/side-nav";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text, TextVariant } from "@amzn/stencil-react-components/text";
import { SearchField } from "@amzn/uxm-stencil-react-components/lib/src/stencil";
import { useMessageBannersBox } from "common/components/MessageBannersBox/hook";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { trackUxmCustomEvent } from "common/helpers/analytics-helper";
import { getString } from "common/uistringlabels/uiStringUtils";
import { PERMISSIONS } from "config";
import { useSearchExperience } from "hooks";
import { useFAQData } from "hooks/useFAQData";
import parse from "html-react-parser";
import { ITShopFAQModel } from "lib/models/faq-models";
import { UserPermissions } from "lib/services";
import { CatalogicalSearchRequestContent } from "lib/services/shop-search-service";
import React, { FC, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

const SEND_METRICS = true;
const HANDLER_DEBOUNCE_TIMEOUT = 300;
// Matches one or more whitespace characters for URL-friendly slug conversion
const WHITESPACE_REGEX = /\s+/g;

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSection {
  id: number;
  items: FAQItem[];
}

const FAQPage: FC = () => {
  const useHardwareShop = UserPermissions.current.hasPermission(PERMISSIONS.USE_HARDWARE_SHOP);
  const { categories, loadingFAQ } = useFAQData();
  const history = useHistory();
  const location = useLocation();
  const [sections, setSections] = useState<FAQSection[]>([]);
  const { items, isLoading, searchCatalogItems } = useSearchExperience<ITShopFAQModel>("itshopfaq");
  const [selectedSection, setSelectedSection] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { addErrorBanner } = useMessageBannersBox();

  async function fetchFAQData(): Promise<void> {
    setSections([]);

    const filters: Record<string, any> = {};

    if (!useHardwareShop) {
      filters["domain"] = "software";
    }

    const nextRequest: CatalogicalSearchRequestContent = {
      searchQuery: searchText,
      useSemanticSearch: false,
      caseInsensitive: true,
      sortBy: "category,sortOrder",
      sortDirection: "asc,asc",
      limit: 100,
      offset: 0,
      vectorField: "embeddings",
      filter: JSON.stringify(filters),
    };

    const response = await searchCatalogItems(nextRequest, SEND_METRICS);
    if (!response) {
      addErrorBanner("No response getting the items");
    }
  }

  useEffect(() => {
    if (items && items.length > 0) {
      const sectionsMap = new Map<number, FAQItem[]>();

      items.forEach((item: ITShopFAQModel) => {
        const category = item.category;
        if (!sectionsMap.has(category)) {
          sectionsMap.set(category, []);
        }
        sectionsMap.get(category)!.push({
          question: item.question,
          answer: item.answer,
        });
      });

      const newSections = Array.from(sectionsMap.entries()).map(([id, items]) => ({
        id,
        items,
      }));

      setSections(newSections);
    }
  }, [items]);

  const hasScrolledToSection = useRef(false);

  useEffect(() => {
    if (hasScrolledToSection.current) return;
    if (sections.length > 0 && categories) {
      const params = new URLSearchParams(location.search);
      const sectionParam = params.get("section");
      if (sectionParam) {
        const matchedSection = Object.values(categories).find(
          (cat) => cat.toLowerCase().replace(WHITESPACE_REGEX, "-") === sectionParam.toLowerCase()
        );
        if (matchedSection) {
          hasScrolledToSection.current = true;
          // Wait for DOM to be painted before scrolling
          requestAnimationFrame(() => {
            scrollToSection(matchedSection);
          });
        }
      }
    }
  }, [sections, categories]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (loadingFAQ || !categories) return;
    setIsInitialized(true);
  }, [categories, loadingFAQ]);

  useEffect(() => {
    if (isInitialized) {
      // Debounce API calls to prevent multiple requests
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        fetchFAQData();
      }, HANDLER_DEBOUNCE_TIMEOUT); // Short debounce to batch rapid state changes
    }
  }, [searchText, isInitialized]);

  const scrollToSection = (sectionTitle: string) => {
    setSelectedSection(sectionTitle);
    const element = document.getElementById(`section-${sectionTitle.replace(WHITESPACE_REGEX, "-").toLowerCase()}`);
    if (element) {
      const elementPosition = element.offsetTop;
      const offsetPosition = elementPosition - 100; // Adjust for top bar height

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const renderBanner = () => {
    return (
      <>
        <Breadcrumbs
          data-test-id="faq-breadcrumbs"
          onCrumbClick={
            /* istanbul ignore next */
            (e, crumb: CrumbProps) => {
              if (crumb.url) {
                e.preventDefault();
                history.push(crumb.url);
              }
            }
          }
          crumbs={[{ title: "IT Shop", url: "/" }, { title: "FAQs" }]}
        />
        <Text data-test-id="faq-title" variant={TextVariant.Display} margin={["10px", "0", "20px", "0"]}>
          IT Shop FAQs
        </Text>
        <Text
          data-test-id="faq-description"
          variant={TextVariant.BodyMD}
          style={{ marginTop: "10px", whiteSpace: "pre-line" }}
        >
          {getString("faq.description")}
        </Text>
      </>
    );
  };

  const renderTitle = ({ titleText, toggle }: { titleText?: string; toggle: () => void }) => (
    <Text data-test-id="faq-expander-title" variant={TextVariant.BodyLGInteractive} onClick={toggle}>
      {titleText ? parse(titleText) : null}
    </Text>
  );

  const renderExpander = (item: FAQItem, index: number) => {
    return (
      <View key={index} data-test-id={`faq-item-${index}`} style={{ marginTop: "16px", marginBottom: "16px" }}>
        <View style={{ marginBottom: "16px" }}>
          <Expander
            data-test-id={`faq-expander-${index}`}
            titleText={item.question}
            renderTitle={renderTitle}
            contentPadding={[0, 0, 0, "32px"]}
            onToggle={() => {
              trackUxmCustomEvent("faq-question-selected", { question: item.question });
            }}
          >
            <Text data-test-id={`faq-answer-${index}`} variant={TextVariant.BodySM}>
              {parse(item.answer)}
            </Text>
          </Expander>
        </View>
        <Hr data-test-id={`faq-divider-${index}`} />
      </View>
    );
  };

  const renderSection = (section: FAQSection, index: number) => {
    const sectionId = `section-${categories[section.id].replace(WHITESPACE_REGEX, "-").toLowerCase()}`;
    return (
      <Container key={index} data-test-id={`faq-section-${index}`} id={sectionId} style={{ marginBottom: "80px" }}>
        <Text
          data-test-id={`faq-section-title-${index}`}
          variant={TextVariant.HeadingLG}
          style={{ marginTop: "10px", marginBottom: "10px" }}
        >
          {categories[section.id]}
        </Text>
        {section.items.map((item, itemIndex) => renderExpander(item, itemIndex))}
      </Container>
    );
  };

  const renderSections = () => {
    return (
      <Col data-test-id="faq-sections-container" width={"70%"}>
        {isLoading && <Spinner size={SpinnerSize.Medium} />}
        {!isLoading &&
          sections
            .sort((a, b) => categories[a.id].localeCompare(categories[b.id]))
            .map((section, index) => renderSection(section, index))}
      </Col>
    );
  };

  const renderSearchTitles = () => {
    const sortedSections = [...sections].sort((a, b) => categories[a.id].localeCompare(categories[b.id]));
    return (
      <SideNavSection data-test-id="faq-nav-section" label="">
        {sortedSections.map((section, index) => (
          <SideNavItem
            key={index}
            data-test-id={`faq-nav-item-${index}`}
            href="#"
            isSelected={selectedSection === categories[section.id]}
            onClick={(e) => {
              e.preventDefault();
              scrollToSection(categories[section.id]);
              trackUxmCustomEvent("faq-section-selected", { section: categories[section.id] });
            }}
          >
            {categories[section.id]}
          </SideNavItem>
        ))}
      </SideNavSection>
    );
  };

  const renderSideNav = () => {
    return (
      <Col data-test-id="faq-sidebar" style={{ position: "sticky", top: "80px", height: "fit-content" }}>
        <SearchField
          data-test-id="faq-search-bar"
          id="faq-search-bar"
          placeholder="Search"
          touchpoint="faq-search"
          onChange={setSearchText}
          query={searchText}
          shouldShowListOnNoResults={false}
        />
        {renderSearchTitles()}
      </Col>
    );
  };

  return (
    <PageContainer data-test-id="faq-page-container" paddingHorizontal={0} paddingTop={0} isFullWidth={true}>
      <Container
        data-test-id="faq-banner-container"
        className="faq-banner"
        justifyContent="space-between"
        paddingHorizontal={"32px"}
        paddingBottom={0}
      >
        <PageMainContainer data-test-id="faq-banner-main">{renderBanner()}</PageMainContainer>
      </Container>
      <PageMainContainer data-test-id="faq-main-container">
        <Row
          data-test-id="faq-content-row"
          margin={["50px", 0, "50px", 0]}
          display="flex"
          alignItems="flex-start"
          justifyContent="center"
          gridGap={"32px"}
        >
          {loadingFAQ && <Spinner size={SpinnerSize.Medium} />}
          {!loadingFAQ && (
            <>
              {renderSections()}
              {renderSideNav()}
            </>
          )}
        </Row>
      </PageMainContainer>
    </PageContainer>
  );
};

export default FAQPage;
